import FileDownload from 'js-file-download';
import { MouseEvent, useMemo, useState } from 'react';

import { Button } from '../../../cdl/Button/Button';
import { Callout } from '../../../cdl/Callout/Callout';
import { DateRangePicker } from '../../../cdl/DateRangePicker/DateRangePicker';
import { Modal } from '../../../cdl/Modal/Modal';
import { Text } from '../../../cdl/Text/Text';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { ButtonGroup } from '../../../common/buttons/ButtonGroup';
import { CustomerGroupSelect } from '../../../common/form-elements/CustomerGroupSelect/CustomerGroupSelect';
import { localEndOfDayAsUtcIso, localStartOfDayAsUtcIso } from '../../../common/helpers/dateFns.helper';
import { extractFileName } from '../../../common/helpers/extractFileName.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconExport } from '../../../common/icons/cdl/Export';
import { Box } from '../../../common/ui/Box';
import { generateCustomerReportDatePresets } from '../helpers/generateCustomerReportDatePresets';
import { useCustomerReportDownload } from '../hooks/useCustomerReportDownload';

export const CustomerOrderReportExportButton = () => {
    const role = useRole();
    const [popupOpen, setPopupOpen] = useState(false);
    const [dates, setDates] = useState<Date[]>([]);
    const [customerGroupId, setCustomerGroupId] = useState<string | undefined>();
    const { addErrorToast } = useToasts();
    const downloadCustomerReportMutation = useCustomerReportDownload();

    const handlePopupOpenClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setPopupOpen(true);
    };

    const handleDismiss = () => {
        setPopupOpen(false);
    };

    const onDownloadButtonClick = async () => {
        if (dates.length !== 2) {
            return;
        }

        const startDate = localStartOfDayAsUtcIso(dates[0]);
        const endDate = localEndOfDayAsUtcIso(dates[1]);

        downloadCustomerReportMutation.mutateAsync(
            {
                customerGroupId: role.isSuperAdmin() ? customerGroupId : undefined,
                dateDeliveryFrom: startDate,
                dateDeliveryTo: endDate,
            },
            {
                onSuccess: (response) => {
                    FileDownload(
                        response.data,
                        extractFileName(response.headers['content-disposition'] || '', 'Orders'),
                        'application/vnd.ms-excel'
                    );
                    setPopupOpen(false);
                },
                onError: () => {
                    addErrorToast(translate('order.export.errorToast'));
                    setPopupOpen(false);
                },
            }
        );
    };

    const presets = useMemo(generateCustomerReportDatePresets, []);

    return (
        <>
            <Button onClick={handlePopupOpenClick} LeadingIcon={IconExport}>
                {translate('order.export.button')}
            </Button>

            <Modal isOpen={popupOpen} onDismiss={handleDismiss}>
                <Box padding={24} display="grid" rowGap={16}>
                    <Text variant="title">{translate('order.export.title')}</Text>

                    <Callout
                        heading={translate('order.export.calloutHeading')}
                        description={translate('order.export.calloutDescription')}
                    />

                    {role.isSuperAdmin() ? (
                        <CustomerGroupSelect
                            label={translate('order.export.customerGroupLabel')}
                            value={customerGroupId ?? null}
                            onChange={(value) => setCustomerGroupId(value ?? undefined)}
                            isClearable
                        />
                    ) : null}

                    <DateRangePicker presets={presets} dates={dates} onDatesChange={setDates} />

                    <ButtonGroup justifyContent="flex-end">
                        <Button onClick={handleDismiss}>{translate('order.export.cancelButton')}</Button>
                        <Button
                            emphasis="high"
                            onClick={onDownloadButtonClick}
                            isLoading={downloadCustomerReportMutation.isPending}
                            disabled={dates.length !== 2}
                        >
                            {translate('order.export.downloadButton')}
                        </Button>
                    </ButtonGroup>
                </Box>
            </Modal>
        </>
    );
};
